// F-148 §4.3 normalization. Every rule here has an identical SQL twin in
// `receiving__normalize_identifier`, `receiving__normalize_key`, `receiving__clean_text` and
// `receiving__clean_reference` (migration 20260929222338). The code-point lists are explicit in
// both languages (never `\p{Cf}`) so the JS/SQL parity table can assert them one by one.
//
// Character classes are built from numeric code points (never from literal invisible characters
// or `\u` escapes in source) so the source file itself contains no bidi/format/control bytes.

import { RECEIVING_LIMITS } from '../types/stock-receiving';
import { normalizeBarcode as normalizeBarcodeF045, validateBarcode } from './barcode';

type CodePointSpec = number | readonly [number, number];

/** Builds a `[...]` regex character class from code points and inclusive ranges. */
export function codePointClass(specs: readonly CodePointSpec[]): string {
  const parts = specs.map((spec) =>
    typeof spec === 'number'
      ? String.fromCodePoint(spec)
      : `${String.fromCodePoint(spec[0])}-${String.fromCodePoint(spec[1])}`,
  );
  return `[${parts.join('')}]`;
}

export const UTF8_BOM = String.fromCodePoint(0xfeff);

/** §4.3 identifiers: bidi marks/isolates/embeddings + BOM removed before matching. */
const IDENTIFIER_INVISIBLE: readonly CodePointSpec[] = [
  0x061c,
  0x200e,
  0x200f,
  [0x202a, 0x202e],
  [0x2066, 0x2069],
  0xfeff,
];

/** §4.3 free text: only embedding/override/isolate controls are stripped. */
export const TEXT_BIDI_CONTROLS: readonly CodePointSpec[] = [
  [0x202a, 0x202e],
  [0x2066, 0x2069],
];

/** §4.3 shipment reference invisible/format set (SEC-r2 NB2). */
const REFERENCE_INVISIBLE: readonly CodePointSpec[] = [
  0x00ad,
  0x061c,
  0x180e,
  [0x200b, 0x200f],
  [0x202a, 0x202e],
  [0x2060, 0x2064],
  [0x2066, 0x206f],
  0xfeff,
];

/** §4.3 whitespace set used for trimming and key collapsing. */
export const RECEIVING_WHITESPACE: readonly CodePointSpec[] = [
  [0x0009, 0x000d],
  0x0020,
  0x0085,
  0x00a0,
  0x1680,
  [0x2000, 0x200a],
  0x2028,
  0x2029,
  0x202f,
  0x205f,
  0x3000,
];

/** C0 controls (incl. DEL) and C1 controls. */
export const CONTROL_CHARACTERS: readonly CodePointSpec[] = [
  [0x0000, 0x001f],
  [0x007f, 0x009f],
];

const IDENTIFIER_INVISIBLE_RE = new RegExp(codePointClass(IDENTIFIER_INVISIBLE), 'gu');
const TEXT_BIDI_CONTROL_RE = new RegExp(codePointClass(TEXT_BIDI_CONTROLS), 'gu');
const REFERENCE_INVISIBLE_RE = new RegExp(codePointClass(REFERENCE_INVISIBLE), 'gu');
const WHITESPACE_CLASS = codePointClass(RECEIVING_WHITESPACE);
const TRIM_RE = new RegExp(`^${WHITESPACE_CLASS}+|${WHITESPACE_CLASS}+$`, 'gu');
const COLLAPSE_RE = new RegExp(`${WHITESPACE_CLASS}+`, 'gu');
const CONTROL_RE = new RegExp(codePointClass(CONTROL_CHARACTERS), 'u');

const ARABIC_INDIC_ZERO = 0x0660;
const EXTENDED_ARABIC_INDIC_ZERO = 0x06f0;
const ARABIC_INDIC_RE = new RegExp(
  codePointClass([
    [ARABIC_INDIC_ZERO, ARABIC_INDIC_ZERO + 9],
    [EXTENDED_ARABIC_INDIC_ZERO, EXTENDED_ARABIC_INDIC_ZERO + 9],
  ]),
  'gu',
);

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** U+0660–0669 and U+06F0–06F9 → ASCII digits. */
export function arabicIndicToAscii(value: string): string {
  return value.replace(ARABIC_INDIC_RE, (ch) => {
    const cp = ch.codePointAt(0) ?? 0;
    const base = cp >= EXTENDED_ARABIC_INDIC_ZERO ? EXTENDED_ARABIC_INDIC_ZERO : ARABIC_INDIC_ZERO;
    return String(cp - base);
  });
}

export function trimReceivingWhitespace(value: string): string {
  return value.replace(TRIM_RE, '');
}

export function hasControlCharacter(value: string): boolean {
  return CONTROL_RE.test(value);
}

export function isUuid(value: string): boolean {
  return UUID_RE.test(value);
}

/**
 * §4.3 identifiers and keys: NFKC → remove bidi/BOM set → Arabic-Indic digits → trim.
 * No zero stripping and no case folding (F045 barcode match is exact).
 */
export function normalizeIdentifier(value: string | null | undefined): string {
  if (value == null) return '';
  const nfkc = String(value).normalize('NFKC');
  return trimReceivingWhitespace(arabicIndicToAscii(nfkc.replace(IDENTIFIER_INVISIBLE_RE, '')));
}

/** §4.3 reference and supplier keys: identifier rules + collapse internal whitespace + lower. */
export function normalizeKey(value: string | null | undefined): string {
  return normalizeIdentifier(value).replace(COLLAPSE_RE, ' ').toLowerCase();
}

export type CleanTextResult =
  | { ok: true; value: string | null }
  | { ok: false; error: 'control_character' | 'too_long' };

/**
 * §4.3 free text: NFC, strip bidi embedding/override/isolate controls, reject any other C0/C1
 * control. Stored as authored otherwise (trimmed ends; blank → null).
 */
export function cleanFreeText(
  value: string | null | undefined,
  maxLength: number,
): CleanTextResult {
  if (value == null) return { ok: true, value: null };
  const stripped = String(value).normalize('NFC').replace(TEXT_BIDI_CONTROL_RE, '');
  if (hasControlCharacter(stripped)) return { ok: false, error: 'control_character' };
  const trimmed = trimReceivingWhitespace(stripped);
  if (trimmed.length === 0) return { ok: true, value: null };
  if (Array.from(trimmed).length > maxLength) return { ok: false, error: 'too_long' };
  return { ok: true, value: trimmed };
}

/**
 * §4.3 `cleanReference` (SEC-r2 NB2, normalize-to-blank): NFC → remove invisible/format set →
 * trim whitespace set → null when empty or when the normalized key is empty.
 * Control characters and length are NOT checked here; see `validateShipmentReference`.
 */
export function cleanReference(value: string | null | undefined): string | null {
  if (value == null) return null;
  const cleaned = trimReceivingWhitespace(
    String(value).normalize('NFC').replace(REFERENCE_INVISIBLE_RE, ''),
  );
  if (cleaned.length === 0) return null;
  if (normalizeKey(cleaned).length === 0) return null;
  return cleaned;
}

export type ShipmentReferenceValidation =
  | { ok: true; origin: 'system'; value: null; key: null }
  | { ok: true; origin: 'merchant'; value: string; key: string }
  | { ok: false; field: 'shipmentReference'; reason: 'too_long' | 'control_character' };

/** §3.1 merchant reference rules, identical to `receiving_create_draft`/`receiving_save_draft`. */
export function validateShipmentReference(
  value: string | null | undefined,
): ShipmentReferenceValidation {
  const cleaned = cleanReference(value);
  if (cleaned === null) return { ok: true, origin: 'system', value: null, key: null };
  if (hasControlCharacter(cleaned)) {
    return { ok: false, field: 'shipmentReference', reason: 'control_character' };
  }
  if (Array.from(cleaned).length > RECEIVING_LIMITS.maxReferenceLength) {
    return { ok: false, field: 'shipmentReference', reason: 'too_long' };
  }
  const key = normalizeKey(cleaned);
  if (key.length === 0) return { ok: true, origin: 'system', value: null, key: null };
  return { ok: true, origin: 'merchant', value: cleaned, key };
}

export type ReceivingBarcodeNormalization =
  | { ok: true; value: string | null }
  | { ok: false; error: 'invalid_barcode' | 'too_long' };

/**
 * §4.2/§4.3 barcode: identifier normalization, then F045 `normalize_barcode` + `is_valid_barcode`.
 * Blank → null (no barcode supplied).
 */
export function normalizeReceivingBarcode(
  value: string | null | undefined,
): ReceivingBarcodeNormalization {
  const identifier = normalizeIdentifier(value);
  if (identifier.length === 0) return { ok: true, value: null };
  const f045 = normalizeBarcodeF045(identifier);
  const validation = validateBarcode(f045);
  if (!validation.valid) {
    return {
      ok: false,
      error:
        validation.errorKey === 'admin.products.errors.barcodeTooLong'
          ? 'too_long'
          : 'invalid_barcode',
    };
  }
  return { ok: true, value: validation.normalized ?? f045 };
}

export type ReceivingVariantRefNormalization =
  | { ok: true; value: string | null }
  | { ok: false; error: 'invalid_uuid' };

export function normalizeReceivingVariantRef(
  value: string | null | undefined,
): ReceivingVariantRefNormalization {
  const identifier = normalizeIdentifier(value);
  if (identifier.length === 0) return { ok: true, value: null };
  if (!isUuid(identifier)) return { ok: false, error: 'invalid_uuid' };
  return { ok: true, value: identifier.toLowerCase() };
}

export type ReceivingIntegerNormalization =
  | { ok: true; value: number | null }
  | { ok: false; error: 'not_integer' | 'out_of_range' };

/** §4.2 quantities: `^[0-9]{1,7}$` after digit normalization, then a [min, max] range. */
export function normalizeReceivingInteger(
  value: string | null | undefined,
  range: { min: number; max: number },
): ReceivingIntegerNormalization {
  const identifier = normalizeIdentifier(value);
  if (identifier.length === 0) return { ok: true, value: null };
  if (!/^[0-9]{1,7}$/.test(identifier)) return { ok: false, error: 'not_integer' };
  const parsed = Number.parseInt(identifier, 10);
  if (parsed < range.min || parsed > range.max) return { ok: false, error: 'out_of_range' };
  return { ok: true, value: parsed };
}

/** §4.2 supplier_code (≤ 200, identifier rules, evidence only). */
export function normalizeReceivingEvidenceText(value: string | null | undefined): CleanTextResult {
  const identifier = normalizeIdentifier(value);
  if (identifier.length === 0) return { ok: true, value: null };
  if (hasControlCharacter(identifier)) return { ok: false, error: 'control_character' };
  if (Array.from(identifier).length > RECEIVING_LIMITS.maxTextField) {
    return { ok: false, error: 'too_long' };
  }
  return { ok: true, value: identifier };
}
